/** @jsxRuntime classic */
/** @jsx jsx */

import { useEffect, useState } from 'react';
import { css, jsx } from '@emotion/react';
import { Colors, Typography } from '@zapier/design-system';
import { ServiceIcon } from '@zapier/design-system-patterns';
import { labelStyles } from '@zapier/style-encapsulation';
import { BREAKPOINTS, MEDIA_MIN_WIDTH } from '../../../constants';
import { SectionName, useAvroTracking } from '../../../observability';
import { CustomButton } from '../../CustomButton';
import { RichTextHeading } from '../../RichTextHeading';
import { RichTextHtml } from '../../RichTextHtml/RichTextHtml';
import { RichTextHTMLString } from '../types';

export interface AppsSectionProps {
  apps: {
    appProfileUrl: string;
    iconUrl: string;
    name: string;
  }[];
  body: RichTextHTMLString;
  ctaText?: string;
  ctaUrl?: string;
  heading: RichTextHTMLString;
}

const appFocusStyles = css({
  content: '""',
  position: 'absolute',
  border: `2px solid ${Colors.neutral800}`,
  borderRadius: '10px',
  inset: '-1px',
});

const Styles = labelStyles('AppsSection', {
  root: css({
    overflow: 'hidden',
    padding: '48px 0',

    [MEDIA_MIN_WIDTH.small]: {
      padding: '96px 20px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      padding: '128px 20px',
    },
  }),

  container: css({
    maxWidth: '1170px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column-reverse',
    alignItems: 'center',

    [MEDIA_MIN_WIDTH.small]: {
      flexDirection: 'row',
    },
  }),

  content: css({
    flexShrink: 0,
    padding: '0 20px',

    [MEDIA_MIN_WIDTH.small]: {
      maxWidth: '288px',
      padding: 0,
    },

    [MEDIA_MIN_WIDTH.large]: {
      maxWidth: '370px',
    },
  }),

  heading: css({
    ...Typography.pageHeader7,
    color: Colors.earth,
    marginBottom: '16px',

    [MEDIA_MIN_WIDTH.small]: {
      marginBottom: '64px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      ...Typography.pageHeader5,
      marginBottom: '144px',
    },
  }),

  body: css({
    ...Typography.heading,
    fontSize: '16px',
    lineHeight: '22px',
    color: Colors.neutral800,
    marginBottom: '20px',

    [MEDIA_MIN_WIDTH.large]: {
      ...Typography.subHeader3,
      marginBottom: '24px',
    },
  }),

  grid: css({
    display: 'grid',
    gridTemplateRows: 'repeat(4, 1fr)',
    gridAutoFlow: 'column',
    gap: '20px 16px',
    flexShrink: 0,
    padding: '0 20px 40px',
    overflowX: 'hidden',

    [MEDIA_MIN_WIDTH.small]: {
      marginLeft: '80px',
      padding: '16px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      gap: '40px 24px',
      marginLeft: '224px',
    },
  }),

  app: css({
    position: 'relative',
    outline: 'none',

    '&:focus::before': appFocusStyles,
    '&:focus-visible::before': appFocusStyles,
    '&:focus:not(:focus-visible)::before': { display: 'none' },
  }),
});

export function AppsSection(props: AppsSectionProps) {
  const [viewportWidth, setViewportWidth] = useState(0);
  const { emitCTAClickEvent } = useAvroTracking();

  useEffect(() => {
    setViewportWidth(window.innerWidth);
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Returns only apps that will fit in the viewport
  const getApps = () => {
    const rows = 4;
    const overflowColumns = viewportWidth < BREAKPOINTS.small ? 0 : 1;
    const appWidth = viewportWidth < BREAKPOINTS.large ? 60 : 80;
    const gridGap = viewportWidth < BREAKPOINTS.large ? 16 : 24;
    let gridWidth = viewportWidth;

    if (
      viewportWidth >= BREAKPOINTS.small &&
      viewportWidth < BREAKPOINTS.large
    ) {
      gridWidth = viewportWidth - 404;
    } else if (viewportWidth >= BREAKPOINTS.large) {
      gridWidth =
        viewportWidth - Math.max((viewportWidth - 1170) / 2, 20) - 610;
    }

    return props.apps.slice(
      0,
      (Math.floor(gridWidth / (appWidth + gridGap)) + overflowColumns) * rows,
    );
  };

  return (
    <div css={Styles.root}>
      <div css={Styles.container}>
        <div css={Styles.content}>
          <RichTextHeading css={Styles.heading} html={props.heading} tag="h2" />
          <div>
            <div css={Styles.body}>
              <RichTextHtml html={props.body} />
            </div>
            {props.ctaText && props.ctaUrl && (
              <CustomButton
                href={props.ctaUrl}
                isFullWidth={true}
                onClick={() =>
                  emitCTAClickEvent({
                    text: props.ctaText!,
                    href: props.ctaUrl!,
                    section: SectionName.AppsSection,
                  })
                }
              >
                {props.ctaText}
              </CustomButton>
            )}
          </div>
        </div>
        <div css={Styles.grid}>
          {getApps().map((app) => (
            <a
              css={Styles.app}
              href={app.appProfileUrl}
              key={app.appProfileUrl}
            >
              <ServiceIcon
                hasShadow={true}
                serviceName={app.name}
                size={viewportWidth < BREAKPOINTS.large ? 60 : 80}
                src={app.iconUrl}
              />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
